import type { SidebarChildrenProps } from "$root/components/common/Sidebar";
import { Button, IconButton } from "@mdotm/shadcn/mdotm/components";
import { Text, Transition } from "@mdotm/shadcn/legacy/components";
import { toClassName } from "@mdotm/shadcn/legacy/react-extensions";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { stableEmptyObject } from "@mdotm/shadcn/legacy/utils";
import { useMemo, useRef, useState } from "react";
import { SphereAIChatSection } from "./SphereAIChatSection";
import { useChatDockState } from "./chatDock";
import { sphereAiConversationsRepository } from "./repository";
import { sphereAIConfig } from "./config";
import { parseIsoToMs } from "./messageTimestamps";
import { SphereAIHistorySection } from "./SphereAIHistorySection";
import { SphereAIFeedbackSection } from "./SphereAIFeedbackSection";
import { panelLaneStyle } from "./SphereAIPanelLane";
import { SphereAIResumedBar } from "./SphereAIResumedBar";
import { ChatIcon } from "./icons/ChatIcon";
import { HistoryIcon } from "./icons/HistoryIcon";
import { FeedbackIcon } from "./icons/FeedbackIcon";

/**
 * The panel's tabs, by position. The underline measures the tab row's children by index, so the
 * order here IS the render order — named constants keep the two in step and make the per-tab rules
 * below (e.g. where "New question" belongs) readable instead of a bare `tabIndex !== 2`.
 */
const CHAT_TAB = 0;
const HISTORY_TAB = 1;
const FEEDBACK_TAB = 2;

export function SphereAISidebarContent({
	setPosition,
	position,
	onClose,
	open,
}: SidebarChildrenProps): React.JSX.Element {
	const [tabIndex, setTabIndex] = useState(CHAT_TAB);
	// Throws if this environment has no sphere-mcp to talk to — which is why the panel is mounted
	// behind `sphereAIAvailable()` rather than the feature flag alone. See ./config.ts.
	const sphereAI = sphereAIConfig();
	const { canDock, docked, setDocked, fullscreen, toggleFullscreen } = useChatDockState();
	const tabTitlesContainerRef = useRef<HTMLDivElement | null>(null);
	// The conversation id is minted up front (and re-minted per "New question") rather
	// than promoted from null after the first turn. A fresh chat already owns its final
	// id, so finishing a turn never changes `currentChat` — that avoids the key change
	// below that would otherwise remount the whole chat section (a visible flicker).
	const [currentChat, setCurrentChat] = useState<string>(() => crypto.randomUUID());
	// The conversation REOPENED from History, if any. Tracked separately from `currentChat`
	// because "which conversation is this?" can't be answered from the id alone: a chat started
	// here is persisted (and so appears in the history list) from its first turn on, and must
	// still read as a fresh chat. Drives the sub-bar above the thread and the active History row.
	const [resumedChat, setResumedChat] = useState<string | null>(null);

	// Open a History entry: point the chat at that conversation's id (a new id → remounts the chat
	// section, which restores the stored messages for it) and switch to the Chat tab.
	const openConversation = (conversationUuid: string) => {
		if (!conversationUuid) {
			return;
		}
		setResumedChat(conversationUuid);
		setCurrentChat(conversationUuid);
		setTabIndex(CHAT_TAB);
	};

	// Conversation history comes from sphere-mcp (/v1/conversations) through the repository, which
	// also owns the writes — so a rename/delete/favourite (History tab) or a persisted turn (Chat
	// tab) refreshes this list by invalidating it, with no `refetch` to thread down as a prop.
	const { data: conversations, isLoading: isChatHistoryLoading } =
		sphereAiConversationsRepository.conversations.useQuery();

	// Name of the reopened conversation, read from the live list so a rename shows up immediately and
	// deleting it (while open) simply drops the sub-bar — no separate copy of the title to keep in sync.
	const resumed = useMemo(() => {
		if (!resumedChat) {
			return null;
		}
		const conversation = conversations?.find((c) => c.uuid === resumedChat);
		const title = conversation?.name?.trim();
		if (!title) {
			return null;
		}
		// When it happened: the last turn, falling back to when it started for a conversation whose
		// last-message time was never recorded. Same pair the thread anchors its own times to.
		const at = parseIsoToMs(conversation?.lastMessageTime) ?? parseIsoToMs(conversation?.creationTime) ?? null;
		return { title, at };
	}, [resumedChat, conversations]);

	const underlineStyle = useMemo<{ left?: number; width?: number }>(() => {
		if (!open) {
			return stableEmptyObject;
		}
		const selectedTab = tabTitlesContainerRef.current?.children[tabIndex] as HTMLButtonElement | undefined;
		if (!selectedTab) {
			return stableEmptyObject;
		}
		return {
			left: selectedTab.offsetLeft,
			width: selectedTab.offsetWidth,
		};
	}, [tabIndex, open]);

	return (
		/* The lane variables live here, at the panel root, so all three tabs and the thread nested in
		   the Chat tab resolve the same reading width and the same content width — see
		   SphereAIPanelLane. The header rows below stay full-bleed on purpose: they are chrome, and the
		   chat has always had a full-bleed header above its lane. */
		<div className="h-full w-full flex flex-col bg-white" style={panelLaneStyle(fullscreen)}>
			<div className="bg-white border-b border-[var(--palette-N100)] relative z-0">
				{/* Row 1: identity on the left, the window-controls block on the right. */}
				<div className="flex items-center justify-between px-4 pt-3 pb-2">
					<Text type="Body/L/Bold" color={themeCSSVars.palette_B500}>
						Ask Sphere AI
					</Text>
					{/* Window controls, grouped as one unit hard against the right edge: pin, dock, maximize, close.
					    A constant gap makes them read as a single cluster rather than scattered marks.
					    Controls that don't apply in the current mode stay in place but disabled, keeping the group
					    shape stable. Glyphs are placeholders (swapped later); only size/color/spacing are tuned here. */}
					<div className="ml-8 flex items-center gap-4">
						{/* pin — reserve layout space vs. float over the page */}
						<IconButton
							unstyled
							disabled={!canDock || fullscreen}
							onClick={() => setDocked((d) => !d)}
							title={docked ? "Undock chat (float over page)" : "Dock chat (reserve space)"}
							icon={docked ? "unpin" : "pin"}
							color={!canDock || fullscreen ? themeCSSVars.palette_N200 : themeCSSVars.palette_N400}
							size={16}
						/>
						{/* dock — move the panel to the other side; glyph reflects the current side */}
						<IconButton
							unstyled
							disabled={!setPosition || fullscreen}
							onClick={() => setPosition?.(position === "left" ? "right" : "left")}
							title={position === "left" ? "Move panel to the right" : "Move panel to the left"}
							icon={position === "left" ? "ask-position-rightsmall" : "ask-positionleftsmall"}
							color={!setPosition || fullscreen ? themeCSSVars.palette_N200 : themeCSSVars.palette_N400}
							size={16}
						/>
						{/* maximize — toggle fullscreen */}
						<IconButton
							unstyled
							onClick={() => toggleFullscreen()}
							title={fullscreen ? "Exit fullscreen" : "Fullscreen chat"}
							icon={fullscreen ? "contract-ori" : "expand-ori"}
							color={themeCSSVars.palette_N400}
							size={16}
						/>
						{/* close */}
						<IconButton
							unstyled
							onClick={() => onClose?.()}
							title="Close"
							icon="Close"
							color={themeCSSVars.palette_N400}
							size={16}
						/>
					</div>
				</div>
				{/* Row 2: navigation on the left (Chat active w/ underline, History), New question on the right. */}
				{/* `min-h` pins the row to the height of the tallest thing it can hold — the x-small
				    "New question" Button (h-26px) — so the header keeps one height across tabs even where
				    that button isn't rendered. Without it the row collapses to the tabs' own ~18px on the
				    Feedback tab and the whole header, hairline and underline included, jumps up 8px. */}
				<div className="flex min-h-[26px] items-center justify-between px-4 pb-2">
					<div className="flex items-center space-x-6" ref={tabTitlesContainerRef}>
						<button type="button" onClick={() => setTabIndex(CHAT_TAB)} className="flex items-center space-x-2">
							<ChatIcon
								className="**:transition-[fill]"
								color={tabIndex === CHAT_TAB ? themeCSSVars.palette_N700 : themeCSSVars.palette_N400}
							/>
							<Text
								className="transition-colors"
								type="Body/M/Bold"
								color={tabIndex === CHAT_TAB ? themeCSSVars.palette_N700 : themeCSSVars.palette_N400}
							>
								Chat
							</Text>
						</button>
						<button type="button" onClick={() => setTabIndex(HISTORY_TAB)} className="flex items-center space-x-2">
							<HistoryIcon
								className="**:transition-[fill]"
								color={tabIndex === HISTORY_TAB ? themeCSSVars.palette_N700 : themeCSSVars.palette_N400}
							/>
							<Text
								className="transition-colors"
								type="Body/M/Bold"
								color={tabIndex === HISTORY_TAB ? themeCSSVars.palette_N700 : themeCSSVars.palette_N400}
							>
								History
							</Text>
						</button>
						<button type="button" onClick={() => setTabIndex(FEEDBACK_TAB)} className="flex items-center space-x-2">
							<FeedbackIcon
								className="**:transition-[fill]"
								color={tabIndex === FEEDBACK_TAB ? themeCSSVars.palette_N700 : themeCSSVars.palette_N400}
							/>
							<Text
								className="transition-colors"
								type="Body/M/Bold"
								color={tabIndex === FEEDBACK_TAB ? themeCSSVars.palette_N700 : themeCSSVars.palette_N400}
							>
								Feedback
							</Text>
						</button>
					</div>
					{/* Only on the conversation tabs. Starting a new question is a sensible next move while
					    reading the Chat or browsing History, but on Feedback it belongs to nothing on
					    screen — and it sat in the navigation row as if it were part of it. */}
					{tabIndex !== FEEDBACK_TAB && (
						<Button
							size="x-small"
							palette="neutralOutline"
							onClick={() => {
								setResumedChat(null);
								setCurrentChat(crypto.randomUUID());
								setTabIndex(CHAT_TAB);
							}}
						>
							New question
						</Button>
					)}
				</div>
				{/* Active-tab underline sitting on the header's bottom hairline (same pattern as the
				    notification center), tracking the selected tab via its measured offset/width. */}
				<div className="absolute -bottom-px w-full h-px">
					<div
						className="transition-[left,width] absolute z-0 inset-y-0 bg-[var(--palette-N700)]"
						style={underlineStyle}
					/>
				</div>
			</div>

			<div className="flex-1 min-h-0 overflow-hidden relative z-0">
				<Transition
					in={tabIndex === CHAT_TAB}
					duration={1000}
					className="absolute z-0 inset-0 transition-opacity"
					enterFromClassName="opacity-0"
					enterToClassName="opacity-100"
					exitedClassName="hidden"
				>
					{({ className }) => (
						<div className={toClassName(className)}>
							{/* A resumed conversation gets a sub-bar naming it; a fresh chat renders the thread
							    alone, so the column below just takes the remaining height in both cases. */}
							<div className="flex h-full w-full flex-col">
								{resumed != null && <SphereAIResumedBar title={resumed.title} at={resumed.at} />}
								<div className="relative z-0 min-h-0 flex-1">
									<SphereAIChatSection
										key={`chatKey-${currentChat}`}
										api={sphereAI.apiUrl}
										mcpUrl={sphereAI.mcpUrl}
										model={sphereAI.model}
										chatId={currentChat}
									/>
								</div>
							</div>
						</div>
					)}
				</Transition>
				<Transition
					in={tabIndex === HISTORY_TAB}
					duration={1000}
					className="absolute z-0 inset-0 transition-opacity"
					enterFromClassName={{
						"opacity-0": true,
					}}
					enterToClassName="opacity-100"
					exitedClassName="hidden"
				>
					{({ className }) => (
						<div className={toClassName(className)}>
							<SphereAIHistorySection
								chatHistory={conversations ?? []}
								loading={isChatHistoryLoading}
								activeConversationUuid={resumedChat}
								onOpenConversation={openConversation}
							/>
						</div>
					)}
				</Transition>
				<Transition
					in={tabIndex === FEEDBACK_TAB}
					duration={1000}
					className="absolute z-0 inset-0 transition-opacity"
					enterFromClassName="opacity-0"
					enterToClassName="opacity-100"
					exitedClassName="hidden"
				>
					{({ className }) => (
						<div className={toClassName(className)}>
							<SphereAIFeedbackSection />
						</div>
					)}
				</Transition>
			</div>
		</div>
	);
}
